"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
  FiDownload,
  FiSearch,
  FiFilter,
  FiUsers,
  FiMail,
  FiPhone,
  FiMapPin,
  FiBriefcase,
  FiDollarSign,
  FiPlay,
  FiPause,
  FiTrash2,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiEye,
  FiX,
} from "react-icons/fi"

const CofounderTable = () => {
  const [cofounders, setCofounders] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const recordsPerPage = 20
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [statusTarget, setStatusTarget] = useState(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedCofounder, setSelectedCofounder] = useState(null)

  useEffect(() => {
    fetchCofounders()
  }, [])

  const fetchCofounders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/cofounders`)
      setCofounders(response.data)
    } catch (error) {
      console.error("Error fetching cofounders:", error)
    }
  }

  const handleStatusDialogOpen = (cofounder) => {
    setStatusTarget(cofounder)
    setStatusDialogOpen(true)
  }

  const handleStatusUpdate = async () => {
    try {
      const updatedStatus = !statusTarget.hold
      await axios.put(`${process.env.REACT_APP_API_URL}/api/cofounders/${statusTarget._id}/status`, {
        hold: updatedStatus,
      })
      setStatusDialogOpen(false)
      fetchCofounders()
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/cofounders/${selectedItem}`)
      setDeleteDialogOpen(false)
      fetchCofounders()
    } catch (error) {
      console.error("Error deleting cofounder:", error)
    }
  }

  // This function seems to be a duplicate or alternative to handleStatusDialogOpen/handleStatusUpdate.
  // Keeping it as is per instruction "dont change any logics".
  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Hold" : "Active"
      await axios.put(`${process.env.REACT_APP_API_URL}/api/cofounders/${id}/status`, { status: newStatus })
      fetchCofounders()
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(cofounders)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cofounders")
    XLSX.writeFile(workbook, "Cofounder_List.xlsx")
  }

  const handleViewDetails = (cofounder) => {
    setSelectedCofounder(cofounder)
    setDetailsDialogOpen(true)
  }

  const filteredCofounders = cofounders.filter((item) => {
    const term = searchTerm.toLowerCase()
    if (!filter) {
      return Object.values(item).some((val) =>
        Array.isArray(val) ? val.join(" ").toLowerCase().includes(term) : val?.toString().toLowerCase().includes(term),
      )
    }
    const fieldValue = item[filter]
    if (Array.isArray(fieldValue)) {
      return fieldValue.join(" ").toLowerCase().includes(term)
    }
    return fieldValue?.toString().toLowerCase().includes(term)
  })

  const totalPages = Math.ceil(filteredCofounders.length / recordsPerPage)
  const displayedCofounders = filteredCofounders.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Background Pattern - moved to main div for better control */}
      <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />
      <div className="relative z-10 max-w-7xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-3xl p-6 mb-8"
        >
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FiUsers className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  CoFounder Registration
                </h1>
                <p className="text-gray-600 text-lg">Manage and track cofounder applications</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700 bg-white/50 px-4 py-2 rounded-xl border border-gray-200 font-medium shadow-sm">
                Total: <span className="font-semibold text-purple-600">{filteredCofounders.length}</span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadExcel}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 text-lg"
              >
                <FiDownload className="text-xl" />
                Download Excel
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-200 rounded-3xl p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <FiFilter className="text-purple-600 text-xl" />
            <h2 className="text-xl font-semibold text-gray-800">Search & Filter</h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-4 w-full">
            <div className="flex-1 space-y-2">
              <label className="text-sm font-semibold text-gray-700">Filter By</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              >
                <option value="">All Fields</option>
                <option value="fullName">Full Name</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="location">Location</option>
                <option value="role">Role</option>
                <option value="expertise">Expertise</option>
                <option value="experience">Experience</option>
                <option value="industries">Industries</option>
                <option value="investmentCapacity">Investment Capacity</option>
              </select>
            </div>
            <div className="flex-1 space-y-2">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FiSearch className="text-base" />
                Search Term
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search cofounders..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              />
            </div>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8"
        >
          <AnimatePresence>
            {displayedCofounders.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-16 backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6">
                  <FiUsers className="text-gray-400 text-4xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No cofounders found</h3>
                <p className="text-gray-500 text-base">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              displayedCofounders.map((cf, index) => (
                <motion.div
                  key={cf._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-3xl p-6 hover:bg-gray-50 transition-all duration-300 transform hover:scale-[1.02]"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FiUsers className="text-purple-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{cf.fullName || "—"}</h3>
                        <span
                          className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${
                            cf.hold
                              ? "bg-orange-100 text-orange-800 border border-orange-200"
                              : "bg-green-100 text-green-800 border border-green-200"
                          }`}
                        >
                          <div className={`w-2 h-2 rounded-full ${cf.hold ? "bg-orange-500" : "bg-green-500"}`}></div>
                          {cf.hold ? "Hold" : "Active"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiMail className="text-base text-gray-500" />
                      <span className="truncate">{cf.email || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiPhone className="text-base text-gray-500" />
                      <span>{cf.phone || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiMapPin className="text-base text-gray-500" />
                      <span className="truncate">{cf.location || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiBriefcase className="text-base text-gray-500" />
                      <span className="truncate">{cf.role || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiDollarSign className="text-base text-gray-500" />
                      <span className="font-semibold text-green-600">₹{cf.investmentCapacity || "—"}</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleViewDetails(cf)}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-xl text-base font-medium transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <FiEye className="text-lg" />
                      View Details
                    </motion.button>
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleStatusDialogOpen(cf)}
                        className={`flex-1 ${
                          cf.hold
                            ? "bg-gradient-to-r from-green-600 to-emerald-600"
                            : "bg-gradient-to-r from-orange-600 to-amber-600"
                        } text-white px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 flex items-center justify-center gap-1`}
                      >
                        {cf.hold ? <FiPlay className="text-base" /> : <FiPause className="text-base" />}
                        {cf.hold ? "Activate" : "Hold"}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setSelectedItem(cf._id)
                          setDeleteDialogOpen(true)
                        }}
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 text-white px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 flex items-center justify-center gap-1"
                      >
                        <FiTrash2 className="text-base" />
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-gray-200 rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
              }`}
            >
              <FiChevronLeft className="text-xl" />
              Previous
            </motion.button>
            <div className="flex items-center gap-2">
              <span className="text-base text-gray-600">
                Page <span className="font-semibold text-purple-600">{currentPage}</span> of{" "}
                <span className="font-semibold text-purple-600">{totalPages || 1}</span>
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage(currentPage + 1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
              }`}
            >
              Next
              <FiChevronRight className="text-xl" />
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Details Dialog */}
      <AnimatePresence>
        {detailsDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2147483647] p-4" // Increased z-index
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Cofounder Details
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setDetailsDialogOpen(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>
              <div className="p-6">
                {selectedCofounder && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Full Name</label>
                      <input
                        type="text"
                        value={selectedCofounder.fullName || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Email</label>
                      <input
                        type="text"
                        value={selectedCofounder.email || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Phone</label>
                      <input
                        type="text"
                        value={selectedCofounder.phone || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Location</label>
                      <input
                        type="text"
                        value={selectedCofounder.location || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Role</label>
                      <input
                        type="text"
                        value={selectedCofounder.role || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Expertise</label>
                      <input
                        type="text"
                        value={selectedCofounder.expertise || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Experience</label>
                      <input
                        type="text"
                        value={selectedCofounder.experience || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Investment Capacity</label>
                      <input
                        type="text"
                        value={`₹${selectedCofounder.investmentCapacity || ""}`}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-semibold text-gray-700">Industries</label>
                      <input
                        type="text"
                        value={selectedCofounder.industries?.join(", ") || ""}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base"
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {deleteDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4" // Increased z-index
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiTrash2 className="text-red-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-700 text-base mb-6">
                  Are you sure you want to delete this cofounder? This will permanently remove them from the system.
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDeleteDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDeleteConfirm}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-medium transition-all duration-300 text-base"
                  >
                    Yes, Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Update Dialog */}
      <AnimatePresence>
        {statusDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4" // Increased z-index
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiRefreshCw className="text-purple-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Change Status</h3>
                    <p className="text-sm text-gray-600">Update cofounder status</p>
                  </div>
                </div>
                <p className="text-gray-700 text-base mb-6">
                  Are you sure you want to set this cofounder to{" "}
                  <strong className={statusTarget?.hold ? "text-green-600" : "text-orange-600"}>
                    {statusTarget?.hold ? "Active" : "Hold"}
                  </strong>
                  ?
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStatusDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleStatusUpdate}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 text-base"
                  >
                    <FiCheck className="text-lg" />
                    Yes, Confirm
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CofounderTable;